/**
 * Composable for firing custom events to the configured analytics provider.
 *
 *   const { track } = useAnalytics()
 *   track('Signup Clicked', { vertical: 'law-firms', plan: 'growth' })
 *
 * All providers receive the event with the same name + props shape —
 * the plugin handles the per-provider call signature.
 *
 * Safe to call regardless of whether analytics is configured: if the
 * provider is blank or the user has opted out, track() is a silent no-op.
 *
 * Also exposes opt-in/opt-out helpers so the footer "Don't track me" link
 * works without leaking analytics state.
 */

interface AnalyticsWindow {
  plausible?: (event: string, opts?: { props?: Record<string, string | number | boolean> }) => void
  fathom?: { trackEvent: (name: string, opts?: Record<string, unknown>) => void }
  gtag?: (...args: unknown[]) => void
  umami?: { track: (name: string, data?: Record<string, unknown>) => void }
}

export const useAnalytics = () => {
  const { $analyticsProvider } = useNuxtApp()
  const provider = ($analyticsProvider ?? '') as string

  const track = (event: string, props: Record<string, string | number | boolean> = {}) => {
    if (!import.meta.client || !provider) return
    const w = window as unknown as AnalyticsWindow

    try {
      if (provider === 'plausible' && w.plausible) {
        w.plausible(event, Object.keys(props).length ? { props } : undefined)
      } else if (provider === 'fathom' && w.fathom) {
        // Fathom uses event IDs created in dashboard; pass the human name
        // as the event id — owner configures matching events in Fathom UI.
        w.fathom.trackEvent(event, props)
      } else if (provider === 'ga4' && w.gtag) {
        w.gtag('event', event, props)
      } else if (provider === 'umami' && w.umami) {
        w.umami.track(event, props)
      }
    } catch {
      // Never let an analytics failure crash a user action.
    }
  }

  const optOut = () => {
    if (!import.meta.client) return
    localStorage.setItem('analytics-opted-out', '1')
    // Take effect on next navigation — full reload is overkill.
  }

  const optIn = () => {
    if (!import.meta.client) return
    localStorage.removeItem('analytics-opted-out')
  }

  const isOptedOut = () =>
    import.meta.client && localStorage.getItem('analytics-opted-out') === '1'

  return { track, optOut, optIn, isOptedOut, provider }
}
